// Import react libs
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';

import queryClient from '../../utils/query/queryClient';

// Import style file
import { ProductWrapper } from './style';

// Import Global styles
import { Loader } from '../../utils/styles/Atoms';

// Import contexts
import { AuthContext } from '../../utils/contexts/AuthContext';
import { VisitContext } from '../../utils/contexts/VisitContext';

// Import components
import { ErrorMessage, ErrorMessageStack } from '../../components/Error';
import ProductDetails from '../../components/ProductDetails';
import ProductForm from '../../components/ProductForm';

// Import hooks
import {
    useFetchProduct,
    useCreateProduct,
    useDeleteProduct,
    useEditProduct,
    useAddImageToProduct,
    useDeleteImage,
    useHaveProductBeenModified,
} from '../../utils/hooks/products';
import { useLogProductView } from '../../utils/hooks/log';
import { usePreloadImages } from '../../utils/hooks/images';

// Import texts
import errorTexts from '../../texts/error';

function Product({ ICON_WIDTH = 30, mode }) {
    // Get the logged user
    const { isAdmin, isLoading: isAuthLoading } = useContext(AuthContext);

    // Get visit id and log product hook
    const { visitId } = useContext(VisitContext);
    const { logProductView } = useLogProductView();

    // Get a navigator
    const navigate = useNavigate();

    // Get URL GET params
    const { id } = useParams();

    // Get the product
    const {
        product,
        isLoading: isProductLoading,
        isError: isProductError,
        refetchProducts,
    } = useFetchProduct(id);

    // Log product view only when product/visit changes
    const lastLoggedRef = useRef({ productId: null, visitId: null });
    useEffect(() => {
        if (
            product &&
            mode === 'view' &&
            !isAdmin &&
            !isAuthLoading &&
            visitId
        ) {
            const current = { productId: product.id, visitId };
            const prev = lastLoggedRef.current;
            const hasChanged =
                prev.productId !== current.productId ||
                prev.visitId !== current.visitId;

            if (hasChanged) {
                logProductView(current.visitId, current.productId);
                lastLoggedRef.current = current;
            }
        }
    }, [product, visitId, mode, isAdmin, isAuthLoading, logProductView]);

    // Get the product image list and preload them
    const productImageList =
        !isProductLoading && !isProductError && product ? product?.images : [];
    const { isLoading: isPreloadImagesLoading } =
        usePreloadImages(productImageList);

    // Get the product updaters
    const {
        createProduct,
        isLoading: isCreateProductLoading,
        isError: isCreateProductError,
        resetError: resetCreateProductError,
    } = useCreateProduct();
    const {
        deleteProduct,
        isLoading: isDeleteProductLoading,
        isError: isDeleteProductError,
        resetError: resetDeleteProductError,
    } = useDeleteProduct();
    const {
        editProduct,
        isLoading: isEditProductLoading,
        isError: isEditProductError,
        resetError: resetEditProductError,
    } = useEditProduct();
    const {
        addImageToProduct,
        isLoading: isAddImageToProductLoading,
        isError: isAddImageError,
        resetError: resetAddImageError,
    } = useAddImageToProduct();
    const {
        deleteImage,
        isLoading: isDeleteImageLoading,
        isError: isDeleteImageError,
        resetError: resetDeleteImageError,
    } = useDeleteImage();

    // Get helper to know if product have been modified elsewhere
    const {
        checkProductModification,
        isLoading: isCheckProductModificationLoading,
        isError: isCheckProductModificationError,
        haveProductBeenModified,
        resetErrors: resetCheckProductModificationErrors,
    } = useHaveProductBeenModified(product);

    // Store in a state the errors if product name or price are empty while editing
    // / creating product
    const [isNameEmptyError, setIsNameEmptyError] = useState(false);
    const [isPriceEmptyError, setIsPriceEmptyError] = useState(false);

    // Reset every error
    function resetErrors() {
        setIsNameEmptyError(false);
        setIsPriceEmptyError(false);

        resetCreateProductError();
        resetDeleteProductError();
        resetEditProductError();

        resetAddImageError();
        resetDeleteImageError();

        resetCheckProductModificationErrors();
    }

    // Combine the isLoading states
    const isLoading =
        isAuthLoading ||
        isProductLoading ||
        isPreloadImagesLoading ||
        isCreateProductLoading ||
        isDeleteProductLoading ||
        isEditProductLoading ||
        isAddImageToProductLoading ||
        isDeleteImageLoading ||
        isCheckProductModificationLoading;

    // Function to create a new product
    async function handleCreateProduct(
        name,
        description,
        isOffer,
        price,
        images,
    ) {
        // Try to create the product
        const newProductId = await createProduct(
            name,
            description,
            isOffer,
            price,
            images,
        );

        // If no error occurs refetch datas and navigate to new product page
        if (newProductId) {
            // Actualise the product list
            await queryClient.invalidateQueries(['products']);

            navigate(`/product/${newProductId}`);
        }
    }

    // Function to edit a product
    async function handleEditProduct(
        name,
        description,
        isOffer,
        price,
        images,
    ) {
        if (!(await checkProductModification())) {
            await queryClient.invalidateQueries(['products']);
            return;
        }

        // Check wether the new fields are equals or not
        const newName = name === product.name ? null : name;
        const newDescription =
            description === product.description ? null : description;
        const newIsOffer = isOffer === product.isOffer ? null : isOffer;
        const newPrice = price === product.price ? null : price;

        // Try to edit product fileds
        const result = await editProduct(
            product.id,
            newName,
            newDescription,
            newIsOffer,
            newPrice,
        );

        if (!result) {
            return;
        }

        // If at least one of the list is not empty
        if (
            (product.images && product.images.length > 0) ||
            (images && images.length > 0)
        ) {
            // Get the list of removed images comparing their images
            const removedImages = product.images?.filter(
                (oldImage) =>
                    !images?.some((image) => image.id === oldImage.id),
            );

            // Get the list of added image, getting all image with negative id
            const addedImages = images?.filter(
                (image) => typeof image.id === 'number' && image.id < 0,
            );

            // If there is removed images
            if (removedImages && removedImages.length > 0) {
                for (const imageToRemove of removedImages) {
                    // Try to delete the image
                    const removeImageResult = await deleteImage(
                        imageToRemove.id,
                        imageToRemove.path,
                    );

                    // If there is an error set it
                    if (!removeImageResult) {
                        // Actualise the product list
                        await queryClient.invalidateQueries(['products']);
                        await refetchProducts();
                        return;
                    }
                }
            }

            // If there is added images
            if (addedImages && addedImages.length > 0) {
                for (const imageToAdd of addedImages) {
                    // Try to add the image
                    const addImageResult = await addImageToProduct(
                        product.id,
                        imageToAdd.path,
                        imageToAdd.file,
                    );

                    // If there is an error set it
                    if (!addImageResult) {
                        // Actualise the product list
                        await queryClient.invalidateQueries(['products']);
                        await refetchProducts();
                        return;
                    }
                }
            }
        }

        await queryClient.invalidateQueries(['products']);

        navigate(`/product/${product.id}`);
    }

    // Function to delete a product
    async function handleDeleteProduct() {
        if (!(await checkProductModification())) {
            await queryClient.invalidateQueries(['products']);
            return;
        }

        // Try to delet the product and its images
        const result = await deleteProduct(product.id, product.images);

        // If no error occurs refetch datas and navigate to new product page
        if (result) {
            // Actualise the product list
            navigate(`/`);

            queryClient.invalidateQueries(['products']);
        }
    }

    // If a non user try to go to edition page
    if (!isAuthLoading && !isAdmin && (mode === 'edit' || mode === 'create')) {
        return <Navigate to="/" />;
    }

    if (isLoading) {
        // Return the loader if the datas are loading
        return (
            <ProductWrapper>
                <Loader />
            </ProductWrapper>
        );
    }

    // If the admin want to add a product
    if (mode === 'create' && isAdmin) {
        return (
            <ProductWrapper>
                <ErrorMessageStack
                    errorMessages={[
                        isCreateProductError && {
                            message: errorTexts.productCreationError,
                            critical: false,
                        },
                        isNameEmptyError && {
                            message: errorTexts.productNameEmptyError,
                            critical: false,
                        },
                        isPriceEmptyError && {
                            message: errorTexts.productPriceEmptyError,
                            critical: false,
                        },
                    ].filter(Boolean)}
                />
                <ProductForm
                    ICON_WIDTH={ICON_WIDTH}
                    productName=""
                    productDescription=""
                    productImages={null}
                    productIsOffer={false}
                    productPrice=""
                    onSubmit={handleCreateProduct}
                    setIsNameEmptyError={setIsNameEmptyError}
                    setIsPriceEmptyError={setIsPriceEmptyError}
                    resetErrors={resetErrors}
                />
            </ProductWrapper>
        );
    }

    // Return product loading error if the product is empty or failed to be loaded
    if (isProductError || !product) {
        return (
            <ProductWrapper>
                <ErrorMessage
                    critical={true}
                    message={errorTexts.productLoadingError}
                />
            </ProductWrapper>
        );
    }

    // If the user want to see the product
    if (mode === 'view') {
        return (
            <ProductWrapper>
                <ProductDetails
                    ICON_WIDTH={ICON_WIDTH}
                    name={product.name}
                    description={product.description}
                    images={
                        product.images && product.images.length > 0
                            ? product.images.map((image) => ({
                                  id: image.id,
                                  url: image.url,
                                  position: image.position,
                              }))
                            : null
                    }
                    offer={product.is_offer}
                    price={product.price}
                    isAdmin={isAdmin}
                    onEdit={() => navigate(`/product/edit/${product.id}`)}
                />
            </ProductWrapper>
        );
    }

    // If the admin want to edit the product
    if (mode === 'edit' && isAdmin) {
        return (
            <ProductWrapper>
                <ErrorMessageStack
                    errorMessages={[
                        (isEditProductError ||
                            isAddImageError ||
                            isDeleteImageError ||
                            isCheckProductModificationError) && {
                            message: errorTexts.productEditionError,
                            critical: false,
                        },
                        haveProductBeenModified && {
                            message: errorTexts.productHaveBeenModifiedError,
                            critical: false,
                        },
                        isDeleteProductError && {
                            message: errorTexts.productDeletionError,
                            critical: false,
                        },
                        isNameEmptyError && {
                            message: errorTexts.productNameEmptyError,
                            critical: false,
                        },
                        isPriceEmptyError && {
                            message: errorTexts.productPriceEmptyError,
                            critical: false,
                        },
                    ].filter(Boolean)}
                />
                <ProductForm
                    ICON_WIDTH={ICON_WIDTH}
                    productName={product.name}
                    productDescription={product.description}
                    productImages={
                        product.images && product.images.length > 0
                            ? product.images.map((image) => ({
                                  id: image.id,
                                  url: image.url,
                                  position: image.position,
                              }))
                            : null
                    }
                    productIsOffer={product.is_offer}
                    productPrice={product.price}
                    onSubmit={handleEditProduct}
                    onDelete={handleDeleteProduct}
                    setIsNameEmptyError={setIsNameEmptyError}
                    setIsPriceEmptyError={setIsPriceEmptyError}
                    resetErrors={resetErrors}
                />
            </ProductWrapper>
        );
    }
}

export default Product;
